import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import approvalWorkflowMasterService from '../../services/approvalWorkflowMasterService'
import contractTypeMasterService from '../../services/contractTypeMasterService'
import { getUsers } from '../../services/projectService'

const ApprovalWorkflowMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const workflowData = location.state?.workflowData

  const [formData, setFormData] = useState({
    workflowCode: '',
    workflowName: '',
    workflowDescription: '',
    workflowType: 'Contract Approval',
    approvalStages: [],
    workflowSettings: {
      autoStart: true,
      requireAllStages: true,
      allowSkipStages: false,
      notifyOnEachStage: true,
      notifyOnCompletion: true,
      notifyOnRejection: true,
      escalationPolicy: 'Time-based'
    },
    applicableContractTypes: [],
    contractValueRanges: [],
    status: 'Active',
    notes: ''
  })

  const [contractTypes, setContractTypes] = useState([])
  const [users, setUsers] = useState([])
  const [isLoadingWorkflowCode, setIsLoadingWorkflowCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [newStage, setNewStage] = useState({
    stageNumber: 1,
    stageName: '',
    stageDescription: '',
    approver: '',
    approverName: '',
    approverRole: '',
    approverEmail: '',
    approvalCriteria: {
      minAmount: 0,
      maxAmount: 0,
      contractTypes: [],
      requiresAllApprovers: false
    },
    approvalTimeLimit: 48,
    escalationEnabled: false,
    escalationTo: '',
    canDelegate: false,
    canReject: true,
    canReturn: true,
    isRequired: true,
    executionType: 'Sequential'
  })
  const [newValueRange, setNewValueRange] = useState({ minValue: 0, maxValue: 0, currency: 'INR' })

  // Fetch next workflow code (only for new workflows)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchWorkflowCode = async () => {
        try {
          setIsLoadingWorkflowCode(true)
          const response = await approvalWorkflowMasterService.getNextWorkflowCode()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              workflowCode: response.data.workflowCode || response.data.formattedValue || 'AWF0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching workflow code:', error)
          setFormData(prev => ({ ...prev, workflowCode: 'AWF0001' }))
        } finally {
          setIsLoadingWorkflowCode(false)
        }
      }
      fetchWorkflowCode()
    }
  }, [isEditMode, isViewMode])

  // Load contract types and users
  useEffect(() => {
    loadContractTypes()
    loadUsers()
  }, [])

  // Load workflow data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && workflowData?._id) {
      loadWorkflowData(workflowData._id)
    }
  }, [isEditMode, isViewMode, workflowData])

  const loadContractTypes = async () => {
    try {
      const response = await contractTypeMasterService.getAllContractTypes({ status: 'Active', limit: 1000 })
      if (response.success) {
        setContractTypes(response.data || [])
      }
    } catch (error) {
      console.error('Error loading contract types:', error)
    }
  }

  const loadUsers = async () => {
    try {
      const response = await getUsers()
      if (response.success) {
        setUsers(response.data || [])
      }
    } catch (error) {
      console.error('Error loading users:', error)
    }
  }

  const loadWorkflowData = async (id) => {
    try {
      setIsLoading(true)
      const response = await approvalWorkflowMasterService.getApprovalWorkflowById(id)
      if (response.success && response.data) {
        const workflow = response.data
        setFormData({
          workflowCode: workflow.workflowCode || '',
          workflowName: workflow.workflowName || '',
          workflowDescription: workflow.workflowDescription || '',
          workflowType: workflow.workflowType || 'Contract Approval',
          approvalStages: workflow.approvalStages || [],
          workflowSettings: workflow.workflowSettings || {
            autoStart: true,
            requireAllStages: true,
            allowSkipStages: false,
            notifyOnEachStage: true,
            notifyOnCompletion: true,
            notifyOnRejection: true,
            escalationPolicy: 'Time-based'
          },
          applicableContractTypes: workflow.applicableContractTypes?.map(ct => ct._id || ct) || [],
          contractValueRanges: workflow.contractValueRanges || [],
          status: workflow.status || 'Active',
          notes: workflow.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading workflow data:', error)
      toast.error('Failed to load workflow data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('workflowSettings.')) {
      const settingField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        workflowSettings: {
          ...prev.workflowSettings,
          [settingField]: type === 'checkbox' ? checked : value
        }
      }))
    } else if (name === 'applicableContractTypes') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value)
      setFormData(prev => ({
        ...prev,
        applicableContractTypes: selectedOptions
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleStageInputChange = (e, index) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('approvalCriteria.')) {
      const criteriaField = name.split('.')[1]
      setFormData(prev => {
        const updatedStages = [...prev.approvalStages]
        updatedStages[index] = {
          ...updatedStages[index],
          approvalCriteria: {
            ...updatedStages[index].approvalCriteria,
            [criteriaField]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
          }
        }
        return { ...prev, approvalStages: updatedStages }
      })
    } else if (name === 'approver') {
      const selectedUser = users.find(u => u._id === value)
      setFormData(prev => {
        const updatedStages = [...prev.approvalStages]
        updatedStages[index] = {
          ...updatedStages[index],
          approver: value,
          approverName: selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}`.trim() : '',
          approverEmail: selectedUser?.email || ''
        }
        return { ...prev, approvalStages: updatedStages }
      })
    } else if (name === 'contractTypes') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value)
      setFormData(prev => {
        const updatedStages = [...prev.approvalStages]
        updatedStages[index] = {
          ...updatedStages[index],
          approvalCriteria: {
            ...updatedStages[index].approvalCriteria,
            contractTypes: selectedOptions
          }
        }
        return { ...prev, approvalStages: updatedStages }
      })
    } else {
      setFormData(prev => {
        const updatedStages = [...prev.approvalStages]
        updatedStages[index] = {
          ...updatedStages[index],
          [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
        }
        return { ...prev, approvalStages: updatedStages }
      })
    }
  }

  const addStage = () => {
    if (!newStage.stageName.trim()) {
      toast.error('Please enter a stage name')
      return
    }
    
    const stageNumber = formData.approvalStages.length + 1
    setFormData(prev => ({
      ...prev,
      approvalStages: [...prev.approvalStages, { ...newStage, stageNumber }]
    }))
    
    // Reset new stage form
    setNewStage({
      stageNumber: stageNumber + 1,
      stageName: '',
      stageDescription: '',
      approver: '',
      approverName: '',
      approverRole: '',
      approverEmail: '',
      approvalCriteria: {
        minAmount: 0,
        maxAmount: 0,
        contractTypes: [],
        requiresAllApprovers: false
      },
      approvalTimeLimit: 48,
      escalationEnabled: false,
      escalationTo: '',
      canDelegate: false,
      canReject: true,
      canReturn: true,
      isRequired: true,
      executionType: 'Sequential'
    })
  }

  const removeStage = (index) => {
    setFormData(prev => ({
      ...prev,
      approvalStages: prev.approvalStages.filter((_, i) => i !== index).map((stage, idx) => ({
        ...stage,
        stageNumber: idx + 1
      }))
    }))
  }

  const addValueRange = () => {
    if (newValueRange.minValue >= newValueRange.maxValue && newValueRange.maxValue > 0) {
      toast.error('Maximum value must be greater than minimum value')
      return
    }
    
    setFormData(prev => ({
      ...prev,
      contractValueRanges: [...prev.contractValueRanges, { ...newValueRange }]
    }))
    
    setNewValueRange({ minValue: 0, maxValue: 0, currency: 'INR' })
  }

  const removeValueRange = (index) => {
    setFormData(prev => ({
      ...prev,
      contractValueRanges: prev.contractValueRanges.filter((_, i) => i !== index)
    }))
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.workflowName) {
        toast.error('Please fill in all required fields (Workflow Name)')
        return
      }

      if (formData.approvalStages.length === 0) {
        toast.error('Please add at least one approval stage')
        return
      }

      setIsLoading(true)
      
      if (isEditMode && workflowData?._id) {
        const response = await approvalWorkflowMasterService.updateApprovalWorkflow(workflowData._id, formData)
        if (response.success) {
          toast.success('Approval workflow updated successfully!')
          navigate('/contracts/approval-workflow-master')
        } else {
          toast.error(response.message || 'Failed to update approval workflow')
        }
      } else {
        const response = await approvalWorkflowMasterService.createApprovalWorkflow(formData)
        if (response.success) {
          toast.success('Approval workflow created successfully!')
          navigate('/contracts/approval-workflow-master')
        } else {
          toast.error(response.message || 'Failed to create approval workflow')
        }
      }
    } catch (error) {
      console.error('Error saving approval workflow:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save approval workflow')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadWorkflowData(workflowData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        workflowName: '',
        workflowDescription: '',
        workflowType: 'Contract Approval',
        approvalStages: [],
        workflowSettings: {
          autoStart: true,
          requireAllStages: true,
          allowSkipStages: false,
          notifyOnEachStage: true,
          notifyOnCompletion: true,
          notifyOnRejection: true,
          escalationPolicy: 'Time-based'
        },
        applicableContractTypes: [],
        contractValueRanges: [],
        status: 'Active',
        notes: ''
      }))
      setNewStage({
        stageNumber: 1,
        stageName: '',
        stageDescription: '',
        approver: '',
        approverName: '',
        approverRole: '',
        approverEmail: '',
        approvalCriteria: {
          minAmount: 0,
          maxAmount: 0,
          contractTypes: [],
          requiresAllApprovers: false
        },
        approvalTimeLimit: 48,
        escalationEnabled: false,
        escalationTo: '',
        canDelegate: false,
        canReject: true,
        canReturn: true,
        isRequired: true,
        executionType: 'Sequential'
      })
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Contracts', path: '/contracts' },
    { label: 'Approval Workflows', path: '/contracts/approval-workflow-master' },
    { 
      label: isEditMode ? 'Edit Approval Workflow' : isViewMode ? 'View Approval Workflow' : 'New Approval Workflow', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading approval workflow...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Approval Workflow' : isViewMode ? 'View Approval Workflow' : 'New Approval Workflow'}</h1>
          <p>{isEditMode ? 'Update approval workflow' : isViewMode ? 'View approval workflow' : 'Create a new approval workflow'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/contracts/approval-workflow-master')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Approval Workflow Master Entry</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header">
            <h3>Workflow Information</h3>
          </div>
          {/* Row 1: Workflow Code, Workflow Name, Workflow Type, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="workflowCode" className="form-label required">
                Workflow Code
              </label>
              <input
                type="text"
                id="workflowCode"
                name="workflowCode"
                value={isLoadingWorkflowCode ? 'Generating...' : formData.workflowCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="workflowName" className="form-label required">
                Workflow Name
              </label>
              <input
                type="text"
                id="workflowName"
                name="workflowName"
                value={formData.workflowName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
                placeholder="e.g., Standard Contract Approval"
              />
            </div>

            <div className="form-group">
              <label htmlFor="workflowType" className="form-label">
                Workflow Type
              </label>
              <select
                id="workflowType"
                name="workflowType"
                value={formData.workflowType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Contract Approval">Contract Approval</option>
                <option value="Legal Review">Legal Review</option>
                <option value="Financial Approval">Financial Approval</option>
                <option value="Executive Approval">Executive Approval</option>
                <option value="Multi-Level">Multi-Level</option>
                <option value="Custom">Custom</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Workflow Description */}
          <div className="form-group">
            <label htmlFor="workflowDescription" className="form-label">
              Description
            </label>
            <textarea
              id="workflowDescription"
              name="workflowDescription"
              value={formData.workflowDescription}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Brief description of the workflow..."
            />
          </div>

          {/* Approval Stages */}
          <div className="section-header">
            <h3>Approval Stages</h3>
          </div>

          {formData.approvalStages.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {formData.approvalStages.map((stage, index) => (
                <div key={index} style={{ 
                  marginBottom: '16px',
                  padding: '16px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#f8f9fa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ margin: 0, color: 'var(--color-brand-navy, #222448)' }}>Stage {stage.stageNumber}: {stage.stageName}</h4>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => removeStage(index)}
                        style={{
                          padding: '6px 12px',
                          border: 'none',
                          background: '#dc3545',
                          color: '#fff',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Trash2 size={14} />
                        Remove
                      </button>
                    )}
                  </div>
                  
                  <div className="grid-row-4" style={{ marginBottom: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">Approver</label>
                      <select
                        name="approver"
                        value={stage.approver || ''}
                        onChange={(e) => handleStageInputChange(e, index)}
                        disabled={isViewMode}
                        className="form-input"
                      >
                        <option value="">Select Approver</option>
                        {users.map(user => (
                          <option key={user._id} value={user._id}>
                            {user.firstName} {user.lastName} ({user.email})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Approval Time Limit (Hours)</label>
                      <input
                        type="number"
                        name="approvalTimeLimit"
                        value={stage.approvalTimeLimit || 48}
                        onChange={(e) => handleStageInputChange(e, index)}
                        disabled={isViewMode}
                        className="form-input"
                        min="1"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Execution Type</label>
                      <select
                        name="executionType"
                        value={stage.executionType || 'Sequential'}
                        onChange={(e) => handleStageInputChange(e, index)}
                        disabled={isViewMode}
                        className="form-input"
                      >
                        <option value="Sequential">Sequential</option>
                        <option value="Parallel">Parallel</option>
                        <option value="Any One">Any One</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Required</label>
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                        <input
                          type="checkbox"
                          name="isRequired"
                          checked={stage.isRequired !== false}
                          onChange={(e) => handleStageInputChange(e, index)}
                          disabled={isViewMode}
                          style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                        />
                        <label style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                          Stage is required
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label">Min Amount</label>
                      <input
                        type="number"
                        name="approvalCriteria.minAmount"
                        value={stage.approvalCriteria?.minAmount || 0}
                        onChange={(e) => handleStageInputChange(e, index)}
                        disabled={isViewMode}
                        className="form-input"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Max Amount</label>
                      <input
                        type="number"
                        name="approvalCriteria.maxAmount"
                        value={stage.approvalCriteria?.maxAmount || 0}
                        onChange={(e) => handleStageInputChange(e, index)}
                        disabled={isViewMode}
                        className="form-input"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
              <h4 style={{ marginTop: 0, marginBottom: '12px', color: 'var(--color-brand-navy, #222448)' }}>Add New Stage</h4>
              <div className="grid-row-4" style={{ marginBottom: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Stage Name</label>
                  <input
                    type="text"
                    value={newStage.stageName}
                    onChange={(e) => setNewStage({ ...newStage, stageName: e.target.value })}
                    className="form-input"
                    placeholder="e.g., Manager Approval"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Approver</label>
                  <select
                    value={newStage.approver}
                    onChange={(e) => {
                      const selectedUser = users.find(u => u._id === e.target.value)
                      setNewStage({
                        ...newStage,
                        approver: e.target.value,
                        approverName: selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}`.trim() : '',
                        approverEmail: selectedUser?.email || ''
                      })
                    }}
                    className="form-input"
                  >
                    <option value="">Select Approver</option>
                    {users.map(user => (
                      <option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Time Limit (Hours)</label>
                  <input
                    type="number"
                    value={newStage.approvalTimeLimit}
                    onChange={(e) => setNewStage({ ...newStage, approvalTimeLimit: parseFloat(e.target.value) || 48 })}
                    className="form-input"
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Execution Type</label>
                  <select
                    value={newStage.executionType}
                    onChange={(e) => setNewStage({ ...newStage, executionType: e.target.value })}
                    className="form-input"
                  >
                    <option value="Sequential">Sequential</option>
                    <option value="Parallel">Parallel</option>
                    <option value="Any One">Any One</option>
                  </select>
                </div>
              </div>
              <button
                type="button"
                onClick={addStage}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add Stage
              </button>
            </div>
          )}

          {/* Workflow Settings */}
          <div className="section-header">
            <h3>Workflow Settings</h3>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="workflowSettings.autoStart" className="form-label">
                Auto Start
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="workflowSettings.autoStart"
                  name="workflowSettings.autoStart"
                  checked={formData.workflowSettings.autoStart}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="workflowSettings.autoStart" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Auto-start workflow
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="workflowSettings.requireAllStages" className="form-label">
                Require All Stages
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="workflowSettings.requireAllStages"
                  name="workflowSettings.requireAllStages"
                  checked={formData.workflowSettings.requireAllStages}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="workflowSettings.requireAllStages" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Require all stages
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="workflowSettings.notifyOnEachStage" className="form-label">
                Notify on Each Stage
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="workflowSettings.notifyOnEachStage"
                  name="workflowSettings.notifyOnEachStage"
                  checked={formData.workflowSettings.notifyOnEachStage}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="workflowSettings.notifyOnEachStage" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Send notifications
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="workflowSettings.escalationPolicy" className="form-label">
                Escalation Policy
              </label>
              <select
                id="workflowSettings.escalationPolicy"
                name="workflowSettings.escalationPolicy"
                value={formData.workflowSettings.escalationPolicy}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="None">None</option>
                <option value="Time-based">Time-based</option>
                <option value="Priority-based">Priority-based</option>
                <option value="Amount-based">Amount-based</option>
              </select>
            </div>
          </div>

          {/* Applicable Contract Types */}
          <div className="form-group">
            <label htmlFor="applicableContractTypes" className="form-label">
              Applicable Contract Types
            </label>
            <select
              id="applicableContractTypes"
              name="applicableContractTypes"
              value={formData.applicableContractTypes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              multiple
              style={{ minHeight: '100px' }}
            >
              {contractTypes.map(type => (
                <option key={type._id} value={type._id}>
                  {type.contractTypeName} ({type.contractTypeCode})
                </option>
              ))}
            </select>
            <small style={{ color: 'var(--color-text-secondary, #5B6478)', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Hold Ctrl/Cmd to select multiple contract types
            </small>
          </div>

          {/* Contract Value Ranges */}
          {formData.contractValueRanges.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <label className="form-label">Contract Value Ranges</label>
              {formData.contractValueRanges.map((range, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginBottom: '8px',
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>
                    {new Intl.NumberFormat('en-IN', { style: 'currency', currency: range.currency || 'INR' }).format(range.minValue || 0)}
                    {' - '}
                    {range.maxValue > 0 ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: range.currency || 'INR' }).format(range.maxValue) : 'Unlimited'}
                    {' (' + (range.currency || 'INR') + ')'}
                  </div>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => removeValueRange(index)}
                      style={{
                        padding: '6px',
                        border: 'none',
                        background: '#dc3545',
                        color: '#fff',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
                <input
                  type="number"
                  placeholder="Min Value"
                  value={newValueRange.minValue}
                  onChange={(e) => setNewValueRange({ ...newValueRange, minValue: parseFloat(e.target.value) || 0 })}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
                <input
                  type="number"
                  placeholder="Max Value (0 = Unlimited)"
                  value={newValueRange.maxValue}
                  onChange={(e) => setNewValueRange({ ...newValueRange, maxValue: parseFloat(e.target.value) || 0 })}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
                <select
                  value={newValueRange.currency}
                  onChange={(e) => setNewValueRange({ ...newValueRange, currency: e.target.value })}
                  className="form-input"
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
                <button
                  type="button"
                  onClick={addValueRange}
                  style={{
                    padding: '10px 16px',
                    border: 'none',
                    background: '#292752',
                    color: '#fff',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Plus size={16} />
                  Add Range
                </button>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default ApprovalWorkflowMasterForm

